import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import AdditionalInfoSection from 'components/intake/AdditionalInfoSection';
import EpisodeInfoSection from 'components/intake/EpisodeInfoSection';
import PatientInfoSection from 'components/intake/PatientInfoSection';
import { FormMeta, FormValues, PatientFormMode } from 'components/intake/types';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import FormHeader from 'components/shared/form/FormHeader';
import { useForm } from 'context/form';
import { useProfile } from 'context/profile';
import { useEpisodeId } from 'hooks/useEpisodeId';
import Patient from 'models/Patient';

export type PatientFormProps = {
  patient: Patient;
  onNextStep?: () => void;
};

export default function PatientForm({ patient, onNextStep }: PatientFormProps) {
  const episodeId = useEpisodeId();
  const navigate = useNavigate();

  const { profile } = useProfile();

  const { meta, valid, dirty } = useForm<FormValues, FormMeta>();
  const connecting = meta.mode === PatientFormMode.Connecting;
  const editing = meta.mode === PatientFormMode.Edit;

  const title = useMemo(() => {
    if (connecting) return 'Coordinate';
    return meta.mode == PatientFormMode.Edit ? 'Edit' : 'Add';
  }, [connecting, meta.mode]);

  const subtitle = meta.isManagerUser && patient.externalId ? `ID: ${patient.externalId}` : undefined;

  const onCancel = () => {
    switch (meta.mode) {
      case PatientFormMode.Edit:
        navigate(`/patients/${episodeId}`);
        break;
      case PatientFormMode.Connecting:
        navigate('/coordinate');
        break;
      default:
        navigate('/patients');
        break;
    }
  };

  return (
    <>
      <FormHeader title={`${title} Patient`} subtitle={subtitle} />
      <PatientInfoSection disabled={!meta.isManagerUser} />
      <EpisodeInfoSection profile={profile} />
      <AdditionalInfoSection patient={patient} />
      <ButtonGroup>
        <Button variant='ghost' data-cy='clickable' type='button' disabled={false} onClick={onCancel}>
          Cancel
        </Button>
        <Button data-cy='clickable' disabled={!valid || (editing && !dirty)} type='button' onClick={onNextStep}>
          {connecting ? 'Next' : 'Review Patient Details'}
        </Button>
      </ButtonGroup>
    </>
  );
}
